import { onUnmounted } from 'vue';
import { useGuidedTourStore } from './guidedTourStore';

/** Tempo sem digitar que conta como "terminou de pesquisar". */
const TYPING_SETTLE_MS = 700;

/** Abaixo disso a busca ainda não filtrou nada útil; avançar seria mentira. */
const MIN_SEARCH_LENGTH = 3;

/**
 * Faz o tour avançar quando a pessoa executa a ação real na tela.
 *
 * Escuta no documento e sobe do elemento clicado até achar um ancestral com
 * `data-tour`. É de propósito que nenhuma tela de negócio chame o tour: as telas
 * só declaram `data-tour="..."`, e quem liga uma coisa na outra é este arquivo.
 * Assim o tour pode ser apagado inteiro sem tocar em Contratos ou Ocorrências.
 *
 * Captura (`capture: true`) porque vários componentes do naive-ui chamam
 * `stopPropagation` no clique; na fase de captura o evento ainda passa por aqui.
 */
export function useTourActionListener() {
	const tour = useGuidedTourStore();

	let typingTimer: number | undefined;

	/** O `data-tour` mais próximo acima do elemento que recebeu o evento. */
	function findTourId(target: EventTarget | null): string | null {
		if (!(target instanceof Element)) return null;

		return target.closest<HTMLElement>('[data-tour]')?.dataset.tour ?? null;
	}

	function currentActionStep(expected: 'click' | 'input') {
		const step = tour.currentStep;

		if (!step || step.advance !== 'action') return null;
		if ((step.actionEvent ?? 'click') !== expected) return null;

		return step;
	}

	function onClick(event: MouseEvent) {
		const step = currentActionStep('click');

		if (!step) return;

		const id = findTourId(event.target);

		if (id && id === (step.actionTarget ?? step.target)) {
			// A tela ainda vai reagir ao clique (navegar, abrir aba). Avançar no
			// próximo tick evita o balão apontar para o alvo antigo por um quadro.
			window.setTimeout(() => tour.completeAction(id), 0);
		}
	}

	function onInput(event: Event) {
		const step = currentActionStep('input');

		if (!step) return;

		const id = findTourId(event.target);

		if (!id || id !== (step.actionTarget ?? step.target)) return;

		const value = (event.target as HTMLInputElement).value ?? '';

		if (typingTimer) window.clearTimeout(typingTimer);

		if (value.trim().length < MIN_SEARCH_LENGTH) return;

		typingTimer = window.setTimeout(() => tour.completeAction(id), TYPING_SETTLE_MS);
	}

	document.addEventListener('click', onClick, true);
	document.addEventListener('input', onInput, true);

	onUnmounted(() => {
		if (typingTimer) window.clearTimeout(typingTimer);
		document.removeEventListener('click', onClick, true);
		document.removeEventListener('input', onInput, true);
	});
}
